import React, { useEffect, useRef } from "react";
import { servicesData } from "../data/servicesData";
import "./ServicesModal.css";

const ServicesModal = ({ isOpen, onClose, selectedServiceId = null }) => {
  const modalRef = useRef(null);
  const overlayRef = useRef(null);
  const closeButtonRef = useRef(null);
  const previousActiveElement = useRef(null);

  // Focus trap and accessibility
  useEffect(() => {
    if (isOpen) {
      // Store the previously focused element
      previousActiveElement.current = document.activeElement;

      // Focus the close button when modal opens
      setTimeout(() => {
        if (closeButtonRef.current) {
          closeButtonRef.current.focus();
        }
      }, 100);

      // Trap focus within modal
      const handleTabKey = (e) => {
        if (e.key !== "Tab") return;

        const focusableElements = modalRef.current?.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );

        if (!focusableElements || focusableElements.length === 0) return;

        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        if (e.shiftKey) {
          // Shift + Tab
          if (document.activeElement === firstElement) {
            e.preventDefault();
            lastElement.focus();
          }
        } else {
          // Tab
          if (document.activeElement === lastElement) {
            e.preventDefault();
            firstElement.focus();
          }
        }
      };

      // Handle ESC key
      const handleEscape = (e) => {
        if (e.key === "Escape") {
          onClose();
        }
      };

      // Prevent body scroll when modal is open
      document.body.style.overflow = "hidden";

      modalRef.current?.addEventListener("keydown", handleTabKey);
      document.addEventListener("keydown", handleEscape);

      return () => {
        document.body.style.overflow = "";
        modalRef.current?.removeEventListener("keydown", handleTabKey);
        document.removeEventListener("keydown", handleEscape);

        // Restore focus to previous element
        if (previousActiveElement.current) {
          previousActiveElement.current.focus();
        }
      };
    }
  }, [isOpen, onClose]);

  // Handle overlay click
  const handleOverlayClick = (e) => {
    if (e.target === overlayRef.current) {
      onClose();
    }
  };

  if (!isOpen) return null;

  // Filter services if a specific service is selected
  const servicesToDisplay = selectedServiceId
    ? servicesData.filter((service) => service.id === selectedServiceId)
    : servicesData;

  return (
    <div
      ref={overlayRef}
      className="services-modal-overlay"
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="services-modal-title"
    >
      <div
        ref={modalRef}
        className="services-modal-content"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="services-modal-header">
          <h2 id="services-modal-title" className="services-modal-title">
            Nossos <span>Serviços</span>
          </h2>
          <button
            ref={closeButtonRef}
            className="services-modal-close"
            onClick={onClose}
            aria-label="Fechar modal de serviços"
            type="button"
          >
            ×
          </button>
        </div>

        <div className="services-modal-body">
          <div className="services-grid">
            {servicesToDisplay.map((service) => (
              <div key={service.id} className="service-card">
                <div className="service-card-header">
                  <div className="service-card-icon">
                    <i className={service.icon}></i>
                  </div>
                  <h3 className="service-card-title">{service.title}</h3>
                </div>
                <p className="service-card-description">
                  {service.description}
                </p>
                <div className="sub-services-grid">
                  {service.subServices.map((subService, index) => (
                    <div key={index} className="sub-service-card">
                      <img
                        src={subService.image}
                        alt={subService.title}
                        className="sub-service-image"
                        loading="lazy"
                      />
                      <div className="sub-service-content">
                        <h4 className="sub-service-title">
                          {subService.title}
                        </h4>
                        <p className="sub-service-description">
                          {subService.description}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServicesModal;
